import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Modal } from '@/components/Modal';
import { useSwitchTenant } from '@/hooks/useSwitchTenant';

export type PlatformTenant = {
  tenant_id: string;
  slug: string;
  display_name: string;
  edition: 'lite' | 'full';
  status: 'active' | 'suspended' | 'archived';
};

/** AppLayout ヘッダーに埋め込むテナント切替UI (v1.0 Phase4、docs/design/v10-phase4-platform-admin-ui.md §3-2)。
 *  is_cross_tenant_admin のユーザーにのみ表示する。構造は NodeSwitcher.tsx (開閉state + backdrop
 *  クリック + Escape + フォーカストラップ) を踏襲するが、対象がフラットなテナント一覧のためtree実装は使わない。 */
export function TenantSwitcher() {
  const user = useAuthStore((s) => s.user);
  const { switchTenant, switching } = useSwitchTenant();

  const { data } = useQuery<{ tenants: PlatformTenant[] }>({
    queryKey: ['platform', 'tenants'],
    queryFn: () => apiFetch('/platform/tenants'),
    enabled: user?.is_cross_tenant_admin === true,
    staleTime: 60 * 1000,
  });
  const tenants = data?.tenants ?? [];

  const [open, setOpen] = useState(false);
  const [pendingTenant, setPendingTenant] = useState<PlatformTenant | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    // design-and-codeレビューHIGH反映: NodeSwitcher.tsxが持つTabキー折り返し(フォーカストラップ)が
    // 欠落していた指摘への対応。role="dialog"パネルとして同じパターンに揃える
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input, [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  if (user?.is_cross_tenant_admin !== true) return null;

  const currentTenant = tenants.find((t) => t.tenant_id === user.active_tenant_id);

  const doSwitch = async (tenant: PlatformTenant) => {
    const ok = await switchTenant(tenant.tenant_id, tenant.display_name);
    if (ok) {
      setPendingTenant(null);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 min-h-[44px] sm:min-h-[40px] px-2 py-1.5 rounded text-sm text-chrome-text-sub hover:bg-chrome-raised hover:text-white transition max-w-[46vw] sm:max-w-xs"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="テナント切替"
      >
        <Building2 size={16} className="shrink-0" />
        <span className="truncate">{currentTenant?.display_name ?? 'テナント切替'}</span>
        <ChevronDown size={14} className="shrink-0" />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="テナント切替パネル"
          className="fixed inset-x-0 bottom-0 z-[45] max-h-[70vh] w-full overflow-y-auto rounded-t bg-white border-t border-neutral-line shadow-lg sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:top-full sm:mt-1 sm:w-72 sm:max-w-[90vw] sm:max-h-none sm:overflow-hidden sm:rounded sm:border"
        >
          <div className="px-3 py-2 border-b border-neutral-line text-[11px] text-neutral-sub">
            システム管理者としてテナントを切替
          </div>
          <div className="py-1 max-h-72 overflow-y-auto">
            {tenants.length === 0 && (
              <div className="px-3 py-2 text-sm text-neutral-sub">テナントがありません</div>
            )}
            {tenants.map((t) => (
              <button
                key={t.tenant_id}
                type="button"
                onClick={() => { setPendingTenant(t); setOpen(false); }}
                disabled={t.status !== 'active'}
                className="w-full flex items-center justify-between gap-2 min-h-[44px] text-left px-3 py-2 text-sm hover:bg-neutral-panel disabled:opacity-45 disabled:cursor-not-allowed"
              >
                <span className="min-w-0">
                  <span className="block truncate text-neutral-text">{t.display_name}</span>
                  <span className="block text-[11px] text-neutral-sub">{t.edition === 'lite' ? 'Lite版' : 'Full版'}{t.status !== 'active' ? ` ・${t.status}` : ''}</span>
                </span>
                {t.tenant_id === user.active_tenant_id && <Check size={14} className="shrink-0 text-brand-red" />}
              </button>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={pendingTenant !== null}
        title="テナントを切り替えます"
        onClose={() => { if (!switching) setPendingTenant(null); }}
        size="sm"
        closeOnOverlay={!switching}
        footer={
          <>
            <button type="button" onClick={() => setPendingTenant(null)} disabled={switching}
              className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken disabled:opacity-45 disabled:cursor-not-allowed focus-ring min-h-[44px] sm:min-h-[40px]">
              キャンセル
            </button>
            <button type="button" onClick={() => pendingTenant && doSwitch(pendingTenant)} disabled={switching}
              className="h-10 px-5 rounded-md text-white text-[13px] font-extrabold btn-press-primary hover:bg-brand-red-hover disabled:opacity-45 disabled:cursor-not-allowed focus-ring min-h-[44px] sm:min-h-[40px] bg-brand-red">
              {switching ? '切替中…' : '切り替える'}
            </button>
          </>
        }
      >
        <p className="text-sm text-text-body leading-relaxed">
          「{pendingTenant?.display_name}」に切り替えます。現在の作業中データは破棄されます。
        </p>
      </Modal>
    </div>
  );
}
